import moment from "moment";
import { EntFinancialIndicatorsFilter } from "../../models/filters/financialIndicatorsFilter";
import { EntFinancialIndicators } from "../../models/mechanization/financialIndicators";
import { ResponseDataGet } from "../../models/responseDataGet";
import { ResponseDataTest } from "../../models/responseDataTest";
import { agroApi } from "./api";
import { BlobResponse, EntReportPayload } from "../../models/report";
import { Paths } from "../../paths";

export interface FinancialIndicatorsByYears {
  year: number;
  financialIndicators: EntFinancialIndicators[];
}

export interface EntYearsFilter {
  years: number[]
}

export const financialIndicatorsApi = agroApi.injectEndpoints({
  endpoints: (builder) => ({
    getFinancialIndicatorsListFiltered: builder.query<ResponseDataGet<EntFinancialIndicators>, EntFinancialIndicatorsFilter>({
      query: (filter) => ({
        url: `${ Paths.registries.financialindicators }/get-all`,
        method: "POST",
        body: JSON.stringify(filter, (key, value) => {
          if (typeof value === 'string' && /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z/.test(value)) {
            return moment(value).format('YYYY-MM-DD')
          }
          return value;
        })
      }),
    }),
    getFinancialIndicatorsListByYears: builder.query<ResponseDataGet<FinancialIndicatorsByYears>, EntYearsFilter>({
      query: (filter) => ({
        url: `${ Paths.registries.financialindicators }/filter-by-years`,
        method: "POST",
        body: JSON.stringify(filter, (key, value) => {
          if (typeof value === 'string' && /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z/.test(value)) {
            return moment(value).format('YYYY-MM-DD')
          }
          return value;
        })
      }),
    }),
    getFinancialIndicatorsList: builder.query<ResponseDataGet<EntFinancialIndicators>, void>({
      query: () => ({
        url: `${ Paths.registries.financialindicators }`,
        method: 'GET',
      }),
    }),
    getFinancialIndicatorsById: builder.query<ResponseDataTest<EntFinancialIndicators>, number>({
      query: (id) => ({
        url: `${ Paths.registries.financialindicators }/${id}`,
        method: 'GET',
      }),
    }),
    addFinancialIndicators: builder.mutation<ResponseDataTest<EntFinancialIndicators>, EntFinancialIndicators>({
      query: (body) => ({
        url: `${ Paths.registries.financialindicators }`,
        method: "POST",
        body: JSON.stringify(body, (key, value) => {
          if (typeof value === 'string' && /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z/.test(value)) {
            return moment(value).format('YYYY-MM-DD')
          }
          return value;
        })
      }),
    }),
    editFinancialIndicators: builder.mutation<ResponseDataTest<EntFinancialIndicators>, EntFinancialIndicators>({
      query: (body) => ({
        url: `${ Paths.registries.financialindicators }`,
        method: 'PUT',
        body: JSON.stringify(body, (key, value) => {
          if (typeof value === 'string' && /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z/.test(value)) {
            return moment(value).format('YYYY-MM-DD')
          }
          return value;
        })
      }),
    }),
    deleteFinancialIndicators: builder.mutation<ResponseDataTest<EntFinancialIndicators>, number>({
      query: (id) => ({
        url: `${ Paths.registries.financialindicators }/${id}`,
        method: 'DELETE',
      }),
      onQueryStarted: async (id, { dispatch, queryFulfilled }) => { }
    }),
    reportFinancialIndicators: builder.mutation<BlobResponse, EntReportPayload>({
      query: (payload) => {
        const { language, governmentAlias } = payload;

        return {
          url: `/report/financial-indicator`,
          method: "POST",
          params: {
            language,
            governmentAlias,
          },
          body: JSON.stringify(payload),
          responseHandler: (response) => response.blob(),
        };
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useAddFinancialIndicatorsMutation,
  useDeleteFinancialIndicatorsMutation,
  useEditFinancialIndicatorsMutation,
  useLazyGetFinancialIndicatorsListFilteredQuery,
  useGetFinancialIndicatorsListByYearsQuery,
  useReportFinancialIndicatorsMutation
} = financialIndicatorsApi;
